import { Divider, Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';

import { isNativeBackupResource } from '@/app/(dashboard)/inventory/inventoryResourceUtils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

export function SnapshotsSummary({ entity }: { entity: InventoryResource }) {
  const dal = useDAL();
  const { data: snapshotsSummary } = dal.inventory.snapshots.useSummary(
    entity.id
  );
  const { vaults } = useBackupVaults();

  const totalCount =
    snapshotsSummary?.clusterTotalCount ?? snapshotsSummary?.totalCount ?? 0;
  const nativeCount = isNativeBackupResource(entity)
    ? snapshotsSummary?.nativeSnapshotCount || 0
    : 0;
  // For display: vault count excludes native snapshots
  const vaultSnapshotCount = totalCount - nativeCount;

  const oldestSnapshot =
    snapshotsSummary?.clusterOldestSnapshot ?? snapshotsSummary?.oldestSnapshot;
  const latestSnapshot =
    snapshotsSummary?.clusterLatestSnapshot ?? snapshotsSummary?.latestSnapshot;
  // Only show vault list if there are non-native snapshots stored in vaults
  const hasVaultSnapshots =
    vaultSnapshotCount > 0 &&
    snapshotsSummary?.snapshotsPerVault &&
    snapshotsSummary.snapshotsPerVault.length > 0;

  const getIndicatorColor = (vaultId: string | null | undefined) => {
    if (!vaultId) {
      return NativeVaultColor;
    }
    return (
      vaults.find((vault) => vault.id === vaultId)?.backgroundColor || null
    );
  };

  // Don't render anything if no snapshots
  if (totalCount === 0 && !oldestSnapshot && !latestSnapshot) {
    return (
      <Stack className='px-[40px] py-[32px]'>
        <Typography color='text.secondary'>No Eon snapshots yet</Typography>
      </Stack>
    );
  }

  return (
    <Accordion>
      <AccordionSummary
        className='px-[40px] py-[32px]'
        sx={{
          '.Mui-expanded': {
            '.snapshot-count': {
              opacity: '0',
            },
          },
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          className='w-full'
        >
          <Typography>
            {nativeCount > 0 && vaultSnapshotCount > 0
              ? `${vaultSnapshotCount} Eon snapshots in Eon vaults, ${nativeCount} in your AWS Backup Vault`
              : nativeCount > 0
                ? `${nativeCount} snapshots stored in your AWS Backup Vault`
                : `${totalCount} Eon snapshots stored in your vaults`}
          </Typography>
          <Stack
            direction='row'
            alignItems='center'
            gap='12px'
            className='snapshot-count'
          >
            {nativeCount > 0 && (
              <Stack direction='row' alignItems='center' gap='8px'>
                <SnapshotIndicator backgroundColor={NativeVaultColor} />
                <Typography fontWeight='400 !important'>
                  {nativeCount}
                </Typography>
              </Stack>
            )}
            {hasVaultSnapshots &&
              snapshotsSummary?.snapshotsPerVault.map((x) => (
                <Stack
                  key={x.vaultId}
                  direction='row'
                  alignItems='center'
                  gap='8px'
                >
                  <SnapshotIndicator
                    backgroundColor={getIndicatorColor(x.vaultId)}
                  />
                  <Typography fontWeight='400 !important'>{x.count}</Typography>
                </Stack>
              ))}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails className='mb-[16px] ml-[40px] px-[40px]'>
        <Stack
          className='w-full'
          direction='row'
          gap='40px'
          justifyContent={'space-between'}
          divider={
            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderStyle: 'dashed' }}
            />
          }
        >
          <Stack gap='24px' flexGrow={1}>
            <Typography variant='overline'>Snapshot Storage</Typography>
            <Stack gap='12px'>
              {nativeCount > 0 && (
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <AccountAsVaultTag
                    cloudProvider={entity.cloudProvider}
                    region={entity.region}
                  />
                  <Typography>{nativeCount}</Typography>
                </Stack>
              )}
              {hasVaultSnapshots
                ? snapshotsSummary?.snapshotsPerVault.map((x) => (
                    <Stack
                      direction='row'
                      alignItems='center'
                      key={x.vaultId}
                      justifyContent='space-between'
                    >
                      <VaultTag vault={x.vaultId} />
                      <Typography>{x.count}</Typography>
                    </Stack>
                  ))
                : nativeCount === 0 && (
                    <Typography color='text.secondary'>No snapshots</Typography>
                  )}
            </Stack>
          </Stack>
          {(oldestSnapshot || latestSnapshot) && (
            <Stack gap='24px' flexGrow={1}>
              <Typography variant='overline'>Snapshot Date Range</Typography>
              <Stack gap='12px'>
                {oldestSnapshot && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    className='py-[3px]'
                  >
                    <Typography>Oldest</Typography>
                    <Stack direction='row' alignItems='center' gap='8px'>
                      <SnapshotIndicator
                        backgroundColor={getIndicatorColor(
                          oldestSnapshot.vaultId
                        )}
                      />
                      <Typography>
                        {dayjs
                          .utc(oldestSnapshot.pointInTime)
                          .format(DATE_FORMAT)}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
                {latestSnapshot && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    className='py-[3px]'
                  >
                    <Typography>Latest</Typography>
                    <Stack direction='row' alignItems='center' gap='8px'>
                      <SnapshotIndicator
                        backgroundColor={getIndicatorColor(
                          latestSnapshot.vaultId
                        )}
                      />
                      <Typography>
                        {dayjs
                          .utc(latestSnapshot.pointInTime)
                          .format(DATE_FORMAT)}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
